'use client'

import { Container } from '../../src/components/Container'
import { Section } from '../../src/components/Section'
import { Button } from '../../src/components/Button'
import { CTAButton } from '../../src/components/CTAButton'
import { Card } from '../../src/components/Card'
import PhoneMockup from '../../src/components/PhoneMockup'
import { SpeechBubble } from '../../src/components/SpeechBubble'
import { Radar } from '../../src/components/Radar'
import { PillsToPrism } from '../../src/components/PillsToPrism'
import { PlanStatusCard } from '../../src/components/PlanStatusCard'
import { SuitcaseIcon, RingsIcon } from '../../src/components/Icons'
import { PrismToCardBeam } from '../../src/components/PrismToCardBeam'
import { PillsToPrismVertical } from '../../src/components/PillsToPrismVertical'
import FinancialPulse from '../../src/components/FinancialPulse'
import { FadeThrough } from '../../src/components/FadeThrough'
import { FAQ } from '../../src/components/FAQ'
import { Footer } from '../../src/components/Footer'
import { SectionDots } from '../../src/components/SectionDots'
import { GetAppPopover } from '../../src/components/GetAppPopover'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'next/navigation'
import { homepageTranslations, HomepageLocale } from '../../src/translations/homepage'


export default function HomePage() {
  const params = useParams()
  const locale = (params?.locale as string) || 'en'
  const t = homepageTranslations[locale as HomepageLocale] || homepageTranslations.en

  const mainRef = useRef<HTMLDivElement>(null)
  const [cardsVisible, setCardsVisible] = useState(false)
  const cardsRef = useRef<HTMLDivElement>(null)
  const [speechBubblesVisible, setSpeechBubblesVisible] = useState(false)
  const speechBubblesRef = useRef<HTMLDivElement>(null)
  const [selectedStage, setSelectedStage] = useState<'starting' | 'organized'>('starting')
  const [animationEpoch, setAnimationEpoch] = useState(0)
  const phoneStage = selectedStage === 'organized' ? 'organized' : 'beginner'
  const chipColor = selectedStage === 'organized' ? '#88C7DB' : '#C5ECAC'
  const mobileStageBg = selectedStage === 'organized' ? '#88B8E4' : '#D1EDEA'
  const [popoverOpen, setPopoverOpen] = useState(false)


  // Override background for home page - desktop gradient, mobile solid
  useEffect(() => {
    const html = document.documentElement
    const body = document.body
    const originalHtmlBg = html.style.background
    const originalBodyBg = body.style.background
    const originalHtmlTransition = html.style.transition
    const originalBodyTransition = body.style.transition
    const originalMobileColor = html.style.getPropertyValue('--mobile-bg-color')

    const isMobile = window.innerWidth < 1024

    if (isMobile) {
      html.style.transition = 'background 600ms ease'
      body.style.transition = 'background 600ms ease'
      html.style.background = '#D1EDEA'
      body.style.background = '#D1EDEA'
      html.style.setProperty('--mobile-bg-color', '#D1EDEA')

      return () => {
        html.style.background = originalHtmlBg
        body.style.background = originalBodyBg
        html.style.transition = originalHtmlTransition
        body.style.transition = originalBodyTransition
        html.style.setProperty('--mobile-bg-color', originalMobileColor)
      }
    } else {
      // Desktop: transparent for gradient
      html.style.transition = originalHtmlTransition
      body.style.transition = originalBodyTransition
      html.style.background = 'transparent'
      body.style.background = 'transparent'

      return () => {
        html.style.background = originalHtmlBg
        body.style.background = originalBodyBg
        html.style.transition = originalHtmlTransition
        body.style.transition = originalBodyTransition
      }
    }
  }, [])

  useEffect(() => {
    if (window.innerWidth >= 1024) return

    const html = document.documentElement
    const body = document.body
    html.style.transition = 'background 600ms ease'
    body.style.transition = 'background 600ms ease'
    html.style.background = mobileStageBg
    body.style.background = mobileStageBg
    html.style.setProperty('--mobile-bg-color', mobileStageBg)
  }, [mobileStageBg])

  // Scroll-driven background gradient based on centered section
  useEffect(() => {
    const el = mainRef.current
    if (!el) return

    const isMobile = window.innerWidth < 1024

    if (isMobile) {
      // Mobile: set solid color
      el.style.setProperty('--bg1', mobileStageBg)
      el.style.setProperty('--bg2', mobileStageBg)
      return
    }

    // Desktop: dynamic gradient based on scroll
    const setBgFromSection = (section: HTMLElement | null) => {
      if (!section) return
      const bg1 = section.getAttribute('data-bg1') || '#ffffff'
      const bg2 = section.getAttribute('data-bg2') || '#ffffff'
      el.style.setProperty('--bg1', bg1)
      el.style.setProperty('--bg2', bg2)
    }

    const sections: HTMLElement[] = Array.from(el.querySelectorAll('section'))
    const getCenteredSection = () => {
      const viewportCenter = window.innerHeight / 2
      let best: { el: HTMLElement; dist: number } | null = null
      for (const s of sections) {
        const r = s.getBoundingClientRect()
        const center = r.top + r.height / 2
        const dist = Math.abs(center - viewportCenter)
        if (!best || dist < best.dist) best = { el: s, dist }
      }
      return best?.el || null
    }

    // Init on mount
    setBgFromSection(getCenteredSection())

    let ticking = false
    const onScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        setBgFromSection(getCenteredSection())
        ticking = false
      })
    }

    // Listen to main scroll
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [mobileStageBg])

  const handleOpenGetApp = () => {
    setPopoverOpen(true)
  }

  // FAQ data
  const faqItems = [
    {
      question: t.faq1Question,
      answer: t.faq1Answer
    },
    {
      question: t.faq2Question,
      answer: t.faq2Answer
    },
    {
      question: t.faq3Question,
      answer: t.faq3Answer
    },
    {
      question: t.faq4Question,
      answer: t.faq4Answer
    },
    {
      question: t.faq5Question,
      answer: t.faq5Answer
    }
  ]


  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCardsVisible(true)
          observer.disconnect() // Only trigger once
        }
      },
      { threshold: 0.3 } // Trigger when 30% visible
    )

    if (cardsRef.current) {
      observer.observe(cardsRef.current)
    }

    return () => observer.disconnect()
  }, [])

  // Intersection observer for speech bubbles — re-runs on persona switch.
  // setSpeechBubblesVisible(false) is called in the click handler (same React batch
  // as animationEpoch increment) so false is committed before this effect runs.
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSpeechBubblesVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )
    if (speechBubblesRef.current) observer.observe(speechBubblesRef.current)
    return () => observer.disconnect()
  }, [animationEpoch])

  // Change page background when segment switcher changes
  useEffect(() => {
    const el = mainRef.current
    if (!el) return
    const stageBg: Record<string, string> = {
      starting:  'rgba(168, 225, 223, 0.5)',  // teal
      organized: 'rgba(93,  151, 209, 0.5)',  // blue
    }
    const isMobile = window.innerWidth < 1024
    const bg1 = isMobile ? mobileStageBg : stageBg[selectedStage] ?? stageBg.starting
    const bg2 = isMobile ? mobileStageBg : 'var(--baby-powder)'
    // Apply immediately to the CSS variable
    el.style.setProperty('--bg1', bg1)
    el.style.setProperty('--bg2', bg2)
    if (isMobile) {
      document.documentElement.style.background = mobileStageBg
      document.body.style.background = mobileStageBg
      document.documentElement.style.setProperty('--mobile-bg-color', mobileStageBg)
    }
    // Update ALL sections so scroll handler picks up the right color on every section
    el.querySelectorAll('section').forEach(section => {
      section.setAttribute('data-bg1', bg1)
      section.setAttribute('data-bg2', bg2)
    })
  }, [selectedStage, mobileStageBg])

  const segmentPills = (
    [
      { key: 'starting',  label: t.segmentBegin },
      { key: 'organized', label: t.segmentGrow },
    ] as const
  ).map(({ key, label }) => (
    <button
      key={key}
      onClick={() => { setSelectedStage(key); setAnimationEpoch(e => e + 1); setSpeechBubblesVisible(false) }}
      disabled={popoverOpen}
      className="px-4 py-1.5 rounded-full text-[13px] font-display font-medium tracking-wide transition-all duration-200"
      style={
        selectedStage === key
          ? { background: 'var(--night)', color: 'var(--baby-powder)' }
          : { background: 'transparent', color: popoverOpen ? 'rgba(9,20,16,0.42)' : 'var(--night)' }
      }
    >
      {label}
    </button>
  ))
  const speechBubbleTone = selectedStage === 'organized' ? 'grow' : 'begin'
  const radarTiles = selectedStage === 'organized' ? t.currentFrame2GrowTiles : t.currentFrame2BeginTiles
  const refundTile = t.currentFrame2BeginTiles[3]!

  return (
    <>
    <SectionDots scrollRef={mainRef} count={7} />

    {/* Floating segment switcher — persists across all sections */}
    <div className="fixed top-[calc(env(safe-area-inset-top,0px)+1rem)] lg:top-5 left-0 right-0 z-50 flex justify-center pointer-events-none">
      <div
        className={`flex gap-1 rounded-full px-2 py-1.5 border border-white/45 ${popoverOpen ? 'pointer-events-none' : 'pointer-events-auto'}`}
        style={{
          background: 'rgba(255,255,255,0.42)',
          backdropFilter: 'blur(18px) saturate(170%)',
          WebkitBackdropFilter: 'blur(18px) saturate(170%)',
          boxShadow: '0 8px 28px rgba(9,20,16,0.1), inset 0 1px 0 rgba(255,255,255,0.28)',
        }}
      >
        {segmentPills}
      </div>
    </div>

    <main
      ref={mainRef}
      className="min-h-dvh overflow-x-hidden lg:h-dvh lg:overflow-y-scroll lg:snap-y lg:snap-mandatory [scrollbar-gutter:stable] [overscroll-behavior-y:contain] [-webkit-overflow-scrolling:touch]"
      style={{
        background: 'linear-gradient(to bottom, var(--bg1, #B0CCE8), var(--bg2, #B0CCE8))',
        transitionProperty: '--bg1, --bg2',
        transitionDuration: '600ms',
        transitionTimingFunction: 'ease',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {/* Frame 1: Hero */}
      <Section
        className="min-h-screen lg:py-0"
        data-bg1="rgba(168, 225, 223, 0.5)"
        data-bg2="var(--baby-powder)"
      >
        <Container className="flex items-start lg:items-center justify-center min-h-screen pt-32 sm:pt-36 lg:pt-0">
          <div className="flex flex-col lg:flex-row items-center justify-center w-full gap-0 lg:gap-20">
            <div className="text-center lg:text-left max-w-2xl relative">
              <h1 className="text-h1 mb-4">{t.currentHeroTitle}</h1>
              <p className="text-body text-night/70 mb-8">
                <FadeThrough stageKey={selectedStage}>
                  {selectedStage === 'organized'
                    ? t.currentHeroGrowSubtitle
                    : t.currentHeroBeginSubtitle}
                </FadeThrough>
              </p>
              <CTAButton chipColor={chipColor} onClick={handleOpenGetApp} trackingLabel="hero_get_app">{t.heroButton}</CTAButton>
            </div>
            <div className="flex items-center justify-center lg:-ml-8 xl:-ml-16 scale-[0.8] sm:scale-90 lg:scale-100 -mt-4 lg:mt-0">
              <PhoneMockup stage={phoneStage} locale={locale} />
            </div>
          </div>
        </Container>
      </Section>

      {/* Frame 2: Missed Opportunity */}
      <Section
        className="min-h-screen"
        data-bg1="rgba(93, 151, 209, 0.8)"
        data-bg2="var(--baby-powder)"
      >
        <Container className="flex items-center justify-center min-h-screen">
          <div className="w-full max-w-7xl">
            <div className="text-center mb-6 sm:mb-8 lg:mb-12">
              <h2 className="text-h2">
                <FadeThrough stageKey={selectedStage}>
                  {selectedStage === 'organized' ? t.currentFrame2GrowTitle : t.currentFrame2BeginTitle}
                </FadeThrough>
              </h2>
              <p className="lg:hidden mt-3 font-sans text-body font-normal text-night/60">
                <FadeThrough stageKey={selectedStage} delay={60}>
                  {selectedStage === 'organized' ? t.currentFrame2GrowRadar : t.currentFrame2BeginRadar}
                </FadeThrough>
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-4 lg:gap-12 lg:items-center">
              {/* Left column: Intro text */}
              <div className="hidden lg:block lg:w-1/3 text-center">
                <h3 className="text-h4 font-sans text-night mb-6">
                  <FadeThrough stageKey={selectedStage} delay={0}>
                    {selectedStage === 'organized' ? t.currentFrame2GrowIntro : t.currentFrame2BeginIntro}
                  </FadeThrough>
                </h3>
                <h3 className="text-h4 font-sans text-night font-medium">
                  <FadeThrough stageKey={selectedStage} delay={60}>
                    {selectedStage === 'organized' ? t.currentFrame2GrowRadar : t.currentFrame2BeginRadar}
                  </FadeThrough>
                </h3>
              </div>

              {/* Right column: Speech bubbles with radar */}
              <div ref={speechBubblesRef} key={animationEpoch} className="lg:w-2/3 relative flex items-center justify-center sm:min-h-[400px]" style={{ minHeight: '400px' }}>
                {/* Medium screen layout - grid */}
                <div className="hidden sm:grid grid-cols-2 gap-6 lg:hidden relative z-10">
                  <SpeechBubble title={radarTiles[0]!.title} tone={speechBubbleTone} delay={0.2} isVisible={speechBubblesVisible} className="w-full">
                    {radarTiles[0]!.text}
                  </SpeechBubble>

                  <SpeechBubble title={radarTiles[1]!.title} tone={speechBubbleTone} delay={2.0} isVisible={speechBubblesVisible} className="w-full">
                    {radarTiles[1]!.text}
                  </SpeechBubble>

                  <SpeechBubble title={radarTiles[2]!.title} tone={speechBubbleTone} delay={4.0} isVisible={speechBubblesVisible} className="w-full">
                    {radarTiles[2]!.text}
                  </SpeechBubble>

                  {selectedStage !== 'organized' && (
                  <SpeechBubble title={refundTile.title} tone={speechBubbleTone} delay={6.0} isVisible={speechBubblesVisible} className="w-full">
                    {refundTile.text}
                  </SpeechBubble>
                  )}
                </div>

                {/* Large screen layout - vertical stack with radar in middle */}
                <div className="hidden lg:flex flex-col items-center justify-center gap-4 relative z-10">
                  <SpeechBubble title={radarTiles[0]!.title} tone={speechBubbleTone} delay={0.2} isVisible={speechBubblesVisible} className="w-[500px]">
                    {radarTiles[0]!.text}
                  </SpeechBubble>

                  <SpeechBubble title={radarTiles[1]!.title} tone={speechBubbleTone} delay={2.0} isVisible={speechBubblesVisible} className="w-[500px]">
                    {radarTiles[1]!.text}
                  </SpeechBubble>

                  <SpeechBubble title={radarTiles[2]!.title} tone={speechBubbleTone} delay={4.0} isVisible={speechBubblesVisible} className="w-[500px]">
                    {radarTiles[2]!.text}
                  </SpeechBubble>

                  {selectedStage !== 'organized' && (
                  <SpeechBubble title={refundTile.title} tone={speechBubbleTone} delay={6.0} isVisible={speechBubblesVisible} className="w-[500px]">
                    {refundTile.text}
                  </SpeechBubble>
                  )}
                </div>

                {/* Mobile layout - stack vertically */}
                <div className="sm:hidden space-y-6 relative z-10">
                  <SpeechBubble title={radarTiles[0]!.title} tone={speechBubbleTone} delay={0.2} isVisible={speechBubblesVisible} className="w-full">
                    {radarTiles[0]!.text}
                  </SpeechBubble>

                  <SpeechBubble title={radarTiles[1]!.title} tone={speechBubbleTone} delay={2.0} isVisible={speechBubblesVisible} className="w-full">
                    {radarTiles[1]!.text}
                  </SpeechBubble>

                  <SpeechBubble title={radarTiles[2]!.title} tone={speechBubbleTone} delay={4.0} isVisible={speechBubblesVisible} className="w-full">
                    {radarTiles[2]!.text}
                  </SpeechBubble>

                  {selectedStage !== 'organized' && (
                  <SpeechBubble title={refundTile.title} tone={speechBubbleTone} delay={6.0} isVisible={speechBubblesVisible} className="w-full">
                    {refundTile.text}
                  </SpeechBubble>
                  )}
                </div>

                {/* Radar in the center - behind speech bubbles */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
                  {/* Mobile radar - smaller size */}
                  <div className="sm:hidden">
                    <Radar size={150} />
                  </div>
                  {/* Desktop radar - larger size */}
                  <div className="hidden sm:block">
                    <Radar size={300} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* Frame 3: Out of Reach */}
      <Section
        className="min-h-screen"
        data-bg1="rgba(136, 199, 219, 0.5)"
        data-bg2="var(--baby-powder)"
      >
        <Container className="flex items-center justify-center min-h-screen">
          <div className="w-full max-w-7xl relative">
            <div className="text-center mb-6 sm:mb-8 lg:mb-12">
              <h2 className="text-h2">
                <FadeThrough stageKey={selectedStage}>
                  {selectedStage === 'organized' ? t.currentFrame3GrowTitle : t.currentFrame3BeginTitle}
                </FadeThrough>
              </h2>
              <p className="lg:hidden mt-3 font-sans text-body font-normal text-night/60">
                <FadeThrough stageKey={selectedStage} delay={60}>
                  {selectedStage === 'organized' ? t.currentFrame3GrowSubtitle : t.currentFrame3BeginSubtitle}
                </FadeThrough>
              </p>
            </div>

            {/* Mobile/Tablet stacked layout */}
            <div className="block lg:hidden">
              <PillsToPrismVertical key={animationEpoch}
                pills={selectedStage === 'organized' ? t.frame3Pills : t.currentFrame3BeginPills}
                intro={selectedStage === 'organized' ? t.currentFrame3GrowIntro : t.currentFrame3BeginIntro}
                strategist={selectedStage === 'organized' ? t.currentFrame3GrowSubtitle : t.currentFrame3BeginSubtitle}
                frontCard={selectedStage === 'organized'
                  ? { title: t.planSabbatical, planValue: t.currentPlanSabbaticalValue, statusValue: t.planOnTrack, icon: <SuitcaseIcon /> }
                  : { title: t.currentPlanJapan, planValue: t.currentPlanJapanValue, statusValue: t.planOnTrack, icon: null }}
                backLeftCard={selectedStage === 'organized'
                  ? { title: t.planApartment, planValue: t.currentPlanApartmentValue, statusValue: t.planOnTrack, icon: undefined }
                  : { title: t.currentPlanBike, planValue: t.currentPlanBikeValue, statusValue: t.planOnTrack, icon: null }}
                backRightCard={selectedStage === 'organized'
                  ? { title: t.currentPlanRetirement, planValue: t.currentPlanRetirementValue, statusValue: t.planOnTrack, icon: null }
                  : { title: t.currentPlanEmergency, planValue: t.currentPlanEmergencyValue, statusValue: t.planOnTrack, icon: null }}
              />
            </div>

            {/* Desktop layout */}
            <div className="hidden lg:flex flex-col lg:flex-row gap-12 items-start relative">
              {/* Left: Pills with rays connecting to Prism */}
              <div className="flex-1 lg:w-2/3 relative">
                <PillsToPrism key={animationEpoch} pills={selectedStage === 'organized' ? t.frame3Pills : t.currentFrame3BeginPills} />
              </div>

              {/* Right: Content */}
              <div className="flex-1 lg:w-1/3 relative">
                <h3 className="text-h4 font-sans text-night mb-6 text-right">
                  <FadeThrough stageKey={selectedStage} delay={0}>
                    {selectedStage === 'organized' ? t.currentFrame3GrowIntro : t.currentFrame3BeginIntro}
                  </FadeThrough>
                </h3>
                <h3 className="text-h4 font-sans text-night font-medium mb-6 text-right">
                  <FadeThrough stageKey={selectedStage} delay={60}>
                    {selectedStage === 'organized' ? t.currentFrame3GrowSubtitle : t.currentFrame3BeginSubtitle}
                  </FadeThrough>
                </h3>
                {/* Layered cards */}
                <div
                  ref={cardsRef}
                  style={{
                    position: 'relative',
                    width: 440,
                    height: 320,
                    marginTop: 16,
                    zIndex: 1,
                    opacity: cardsVisible ? 1 : 0,
                    transform: cardsVisible ? 'translateY(0)' : 'translateY(8px)',
                    transition: 'opacity 0.6s ease-out 1.8s, transform 0.6s ease-out 1.8s'
                  }}
                >
                  {/* Left back card */}
                  <div
                    style={{
                      position: 'absolute',
                      left: -100,
                      top: 200,
                      transform: 'rotate(-10deg) scale(0.8)',
                      transformOrigin: 'center',
                      zIndex: 1,
                      opacity: cardsVisible ? 0.9 : 0,
                      transition: 'opacity 0.6s ease-out 3.0s'
                    }}
                  >
                    <PlanStatusCard
                      title={selectedStage === 'organized' ? t.planApartment : t.currentPlanBike}
                      icon={selectedStage === 'organized' ? undefined : null}
                      planValue={selectedStage === 'organized' ? t.currentPlanApartmentValue : t.currentPlanBikeValue}
                      statusValue={t.planOnTrack}
                      size="sm"
                    />
                  </div>
                  {/* Right back card */}
                  <div
                    style={{
                      position: 'absolute',
                      right: -100,
                      top: 200,
                      transform: 'rotate(10deg) scale(0.8)',
                      transformOrigin: 'center',
                      zIndex: 1,
                      opacity: cardsVisible ? 0.9 : 0,
                      transition: 'opacity 0.6s ease-out 3.2s'
                    }}
                  >
                    <PlanStatusCard
                      title={selectedStage === 'organized' ? t.currentPlanRetirement : t.currentPlanEmergency}
                      icon={null}
                      planValue={selectedStage === 'organized' ? t.currentPlanRetirementValue : t.currentPlanEmergencyValue}
                      statusValue={t.planOnTrack}
                      size="sm"
                    />
                  </div>
                  {/* Front primary card */}
                  <div id="main-plan-card" style={{ position: 'absolute', left: '50%', top: 60, transform: 'translateX(-50%)', zIndex: 2 }}>
                    <PlanStatusCard
                      title={selectedStage === 'organized' ? t.planSabbatical : t.currentPlanJapan}
                      size="md"
                      icon={selectedStage === 'organized' ? <SuitcaseIcon /> : null}
                      planValue={selectedStage === 'organized' ? t.currentPlanSabbaticalValue : t.currentPlanJapanValue}
                      statusValue={t.planOnTrack}
                    />
                  </div>
                </div>
              </div>
            </div>
            {/* Beam overlay from prism to main card - spans both columns (desktop only) */}
            <div className="hidden lg:block" style={{ zIndex: 0 }}>
              <PrismToCardBeam key={animationEpoch} />
            </div>
          </div>
        </Container>
      </Section>

      {/* Frame 4: At Peace */}
      <Section
        className="min-h-screen"
        data-bg1="rgba(197, 236, 172, 0.5)"
        data-bg2="var(--baby-powder)"
      >
        <Container className="flex flex-col items-center justify-center min-h-screen">
          <div className="w-full max-w-7xl">
            <div className="text-center max-w-4xl mx-auto">
              <h2 className="text-h2 mb-8 sm:mb-12">
                <FadeThrough stageKey={selectedStage}>
                  {selectedStage === 'organized' ? t.currentFrame4GrowTitle : t.currentFrame4BeginTitle}
                </FadeThrough>
              </h2>
            </div>
            <div className="w-full lg:w-1/2 text-center lg:text-left mt-3 md:mt-6 lg:mt-12">
              <h3 className="hidden lg:block text-h4 font-sans text-night mb-6">
                <FadeThrough stageKey={selectedStage} delay={0}>
                  {selectedStage === 'organized' ? t.currentFrame4GrowIntro : t.currentFrame4BeginIntro}
                </FadeThrough>
              </h3>
              <p className="font-sans text-body lg:text-h4 font-normal lg:font-medium text-night/60 lg:text-night">
                <FadeThrough stageKey={selectedStage} delay={60}>
                  {selectedStage === 'organized' ? t.currentFrame4GrowPulse : t.currentFrame4BeginPulse}
                </FadeThrough>
              </p>
            </div>
            <div className="mt-2 md:mt-4">
              <FinancialPulse key={animationEpoch}
                cards={
                  selectedStage === 'organized'
                    ? { ...t.frame4PulseCards, ...t.currentFrame4GrowCards }
                    : { ...t.frame4PulseCards, ...t.currentFrame4BeginCards }
                }
                spendingStatusIcon={selectedStage === 'organized' ? 'arrow-up' : 'arrow-up'}
                goalsStatusIcon={'none'}
                liquidityBufferContent={selectedStage === 'organized' ? (
                  <div className="text-[11px] text-night/80 space-y-0.5 mt-0.5">
                    <div className="flex justify-between gap-3">
                      <span>{t.currentFrame4ReturnRows.stocks}</span><span className="text-emerald-600">+6.5%</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span>{t.currentFrame4ReturnRows.crypto}</span><span className="text-red-500">-16%</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span>{t.currentFrame4ReturnRows.savings}</span><span>2.5%</span>
                    </div>
                    <div className="flex justify-between gap-3 font-medium border-t border-night/10 pt-0.5">
                      <span>{t.currentFrame4ReturnRows.avg}</span><span>4.5%</span>
                    </div>
                  </div>
                ) : undefined}
              />
            </div>
          </div>
        </Container>
      </Section>

      {/* Frame 5: FAQ */}
      <Section
        className="min-h-screen"
        data-bg1="var(--baby-powder)"
        data-bg2="var(--baby-powder)"
      >
        <Container className="flex items-center justify-center min-h-screen">
          <div className="text-center max-w-4xl">
            <h2 className="text-h2 mb-12">{t.faqTitle}</h2>
            <FAQ items={faqItems} />
          </div>
        </Container>
      </Section>

      {/* Frame 6: Join */}
      <Section
        className="min-h-screen"
        data-bg1="rgba(221, 248, 195, 0.5)"
        data-bg2="var(--baby-powder)"
      >
        <Container className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-h2 mb-10">{t.currentFinalTitle}</h2>
            <CTAButton chipColor={chipColor} onClick={handleOpenGetApp} trackingLabel="final_get_app">{t.heroButton}</CTAButton>
          </div>
        </Container>
      </Section>

      {/* Footer */}
      <Section
        className="min-h-fit py-0"
        data-bg1="rgba(221, 248, 195, 0.5)"
        data-bg2="var(--baby-powder)"
      >
        <Footer locale={locale} />
      </Section>

      {/* Get the app (store badges + desktop QR) */}
      <GetAppPopover
        isOpen={popoverOpen}
        onClose={() => setPopoverOpen(false)}
        locale={locale}
        persona={selectedStage === 'organized' ? 'grow' : 'begin'}
      />
    </main>
    </>
  )
}
